/* eslint-disable no-console, class-methods-use-this, no-new, default-case, no-plusplus, prefer-exponentiation-operator, prettier/prettier */
import 'webrtc-adapter';
import { UserAgent, UserAgentState, Registerer, Inviter, SessionState, RegistererState } from 'sip.js';
import ringtoneFile from 'dashboard/assets/audio/ringtone.mp3';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import {
  createTickWorker,
  bindWakeEvents,
} from '../telephony_shared/sipResilience';

// Prazo pro Asterisk confirmar CANCEL/BYE/reject (normal: < 1s).
export const HANGUP_CONFIRM_TIMEOUT_MS = 5000;

/**
 * SIP over WebSocket and WebRTC client for ACI Asterisk.
 * Using SIP.js library for better compatibility.
 *
 * @see https://sipjs.com/api/0.20.0/
 */
export default class AciSipClient {
  constructor(params = {}) {
    this.params = {
      extension: null,
      password: null,
      displayName: null,
      sipServer: 'apijarbis.portcallvoip.com.br',
      wsServer: 'wss://apijarbis.portcallvoip.com.br:38089/ws',
      debug: false,
      onUserAgentAction: () => {},
      onCallEvent: () => {}, // Global callback for all call events
      isPaused: () => false, // Agente em pausa recusa toda chamada recebida
      ...params,
    };

    // Call state
    this.activeCall = null;
    this.direction = null;
    this.startTime = null;
    this.endTime = null;
    this.currentDestination = null;
    this.iceConnectionState = 'new';
    this.failed = false;
    this.localHold = false;
    this.callDisposition = null; // answered, busy, no-answer, cancel, failed
    this.lastCallEndedAt = 0; // Timestamp of last call end (prevents duplicate INVITEs from Asterisk fork)
    this.hangupWatchdog = null;

    // Consultation (attended transfer) state
    this.consultationCall = null;
    this.consultationAudioElement = null;
    this.isAttendedTransferInProgress = false;

    // Audio state
    this.audioPlayerElement = null;
    this.micMuted = false;
    this.outputVolume = 1;
    this.audioUnlocked = false; // Track if audio playback is unlocked

    // Ringtone setup
    this.ringtone = new Audio(ringtoneFile);
    this.ringtone.loop = true;
    this.ringtone.volume = 0.8;

    // Registration state
    this.isRegistered = false;
    this.onCallAction = () => {};
    this.onUserAgentAction = this.params.onUserAgentAction;

    // Auto-reconnect (sem teto de tentativas — backoff capado em 60s; os
    // wake events furam a fila quando aba/rede/máquina voltam)
    this.reconnectAttempts = 0;
    this.reconnectDelay = 3000;
    this.reconnectTimer = null;
    this.intentionallyStopped = false;
    this.isStarting = false;

    // Keep-alive (WebSocket ping) — tick num Web Worker, não em setInterval
    // de página: timers de página são throttled em aba background e o contato
    // expirava no Asterisk. Ver telephony_shared/sipResilience.js.
    this.keepAliveWorker = null;
    this.keepAliveIntervalMs = 25000; // 25 seconds
    // Re-REGISTER proativo a cada N ticks (12 × 25s = 300s) — o refresh
    // interno do SIP.js usa timer de página e atrasa/congela em background.
    this.registerRefreshEveryTicks = 12;
    this.ticksSinceRegister = 0;
    this.unbindWakeEvents = null;

    // SIP.js User Agent
    const sipUri = `sip:${this.params.extension}@${this.params.sipServer}`;


    // Build ICE servers configuration
    const iceServers = this.buildIceServers();

    // Create User Agent
    this.sipUserAgent = new UserAgent({
      uri: UserAgent.makeURI(sipUri),
      transportOptions: {
        server: this.params.wsServer,
        traceSip: this.params.debug,
      },
      authorizationUsername: this.params.extension,
      authorizationPassword: this.params.password,
      displayName: this.params.displayName || this.params.extension,
      sessionDescriptionHandlerFactoryOptions: {
        peerConnectionConfiguration: {
          iceServers,
        },
      },
      delegate: {
        onInvite: invitation => this.handleIncomingCall(invitation),
        onDisconnect: error => this.handleDisconnect(error),
        onConnect: () => this.handleConnect(),
      },
      logLevel: this.params.debug ? 'debug' : 'error',
    });

    // Create Registerer
    this.registerer = new Registerer(this.sipUserAgent, {
      expires: 600,
    });

    this.setupRegistererEvents();

    // Reconexão orientada a evento (rede voltou / aba visível / resume) —
    // por último: se a construção do UserAgent lançar, nenhum listener global
    // fica preso a um objeto meio-construído.
    this.unbindWakeEvents = bindWakeEvents(reason => this.handleWake(reason));
  }

  // ==================== ICE SERVERS ====================

  /**
   * Build ICE servers configuration
   * Supports STUN and TURN servers from params or defaults
   */
  buildIceServers() {
    const iceServers = [];

    // Add TURN server if configured
    // TURN is essential for NAT traversal when direct connection fails
    if (this.params.turnServer) {
      const turnConfig = {
        urls: this.params.turnServer,
      };

      // Add credentials if provided
      if (this.params.turnUsername) {
        turnConfig.username = this.params.turnUsername;
      }
      if (this.params.turnPassword) {
        turnConfig.credential = this.params.turnPassword;
      }

      iceServers.push(turnConfig);
    } else {
      console.warn('[AciSipClient] ⚠️ TURN server não configurado - conexões podem falhar em redes restritivas');
    }

    return iceServers;
  }

  // ==================== RINGTONE ====================

  /**
   * Unlock audio playback - must be called from a user interaction event
   * Browsers require user interaction before allowing audio playback
   */
  unlockAudio() {
    if (this.audioUnlocked) return;

    // Play and immediately pause to unlock audio
    const unlockPromise = this.ringtone.play();
    if (unlockPromise) {
      unlockPromise
        .then(() => {
          this.ringtone.pause();
          this.ringtone.currentTime = 0;
          this.audioUnlocked = true;
        })
        .catch(() => {
          // Still not unlocked, will try again on next interaction
        });
    }
  }

  playRingtone() {
    if (!this.audioUnlocked) {
      console.warn('[AciSipClient] ⚠️ Áudio não desbloqueado - ringtone não tocará. Clique na página primeiro.');
      // Try to use Web Notification API as fallback
      this.showIncomingCallNotification();
      return;
    }

    this.ringtone.play().catch(err => {
      console.error('[AciSipClient] Erro ao tocar ringtone:', err);
      // Fallback to notification
      this.showIncomingCallNotification();
    });
  }

  stopRingtone() {
    this.ringtone.pause();
    this.ringtone.currentTime = 0;
  }

  /**
   * Show browser notification for incoming call when audio can't play
   */
  showIncomingCallNotification() {
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('📞 Chamada recebida', {
        body: 'Você tem uma chamada entrando',
        icon: '/favicon.ico',
        requireInteraction: true,
        tag: 'incoming-call',
      });
    }
  }

  // ==================== SETUP ====================

  setupRegistererEvents() {

    this.registerer.stateChange.addListener(state => {

      switch (state) {
        case RegistererState.Registered:
          this.markRegistered();
          break;

        case RegistererState.Unregistered:
          this.isRegistered = false;
          this.stopKeepAlive();
          this.onUserAgentAction({ type: 'unregistered' });
          break;

        case RegistererState.Terminated:
          this.isRegistered = false;
          this.stopKeepAlive();
          this.onUserAgentAction({ type: 'unregistered' });
          this.scheduleReconnectIfNeeded();
          break;
      }
    });
  }

  markRegistered() {
    this.isRegistered = true;
    this.reconnectAttempts = 0;
    this.startKeepAlive();
    this.onUserAgentAction({ type: 'registered' });
  }

  // ==================== KEEP-ALIVE ====================

  startKeepAlive() {
    this.stopKeepAlive();
    this.ticksSinceRegister = 0;

    this.keepAliveWorker = createTickWorker(this.keepAliveIntervalMs, () =>
      this.handleKeepAliveTick()
    );
  }

  handleKeepAliveTick() {
    if (!this.sipUserAgent || !this.sipUserAgent.transport) return;
    try {
      // Send CRLF ping to keep WebSocket alive
      this.sipUserAgent.transport.send('\r\n\r\n');
    } catch (error) {
      console.error('[AciSipClient] Erro ao enviar keep-alive:', error);
    }

    this.ticksSinceRegister += 1;
    if (this.ticksSinceRegister >= this.registerRefreshEveryTicks) {
      this.ticksSinceRegister = 0;
      this.refreshRegistration();
    }
  }

  // REGISTER de renovação fora do agendador interno do SIP.js (que roda em
  // timer de página e atrasa em background). Idempotente: refresh só reseta
  // o expires do contato no Asterisk.
  refreshRegistration() {
    if (!this.isRegistered) return;
    try {
      this.registerer.register().catch(error => {
        console.warn('[AciSipClient] Register refresh falhou:', error.message);
      });
    } catch (error) {
      // register() lança se já há um REGISTER em voo — o próximo tick renova
      console.warn('[AciSipClient] Register refresh pulado:', error.message);
    }
  }

  stopKeepAlive() {
    if (this.keepAliveWorker) {
      this.keepAliveWorker.stop();
      this.keepAliveWorker = null;
    }
  }

  handleConnect() {
    this.onUserAgentAction({ type: 'connected' });
  }

  handleDisconnect(error) {
    this.isRegistered = false;
    this.stopKeepAlive();
    this.onUserAgentAction({ type: 'disconnected', payload: { error } });
    // O SIP.js não encerra a sessão quando o WebSocket cai: o BYE/487 do
    // Asterisk se perde com o socket e a chamada ficava viva pra sempre no
    // client — dialer preso em "chamando" e toda discagem nova recusada com
    // "Ja existe uma chamada ativa" até o F5 (chamado Aliança/conta 161).
    // Chamada JÁ atendida fica: a mídia (WebRTC) independe do WebSocket e
    // pode seguir viva num blip de rede — derrubá-la aqui seria regressão.
    // Se a sinalização morreu de vez, o watchdog do hangup destrava.
    if (this.activeCall && this.activeCall.state !== SessionState.Established) {
      this.forceEndCall('conexão com a telefonia caiu durante a chamada');
    }
    this.scheduleReconnectIfNeeded();
  }

  // ==================== CONNECTION ====================

  async start() {
    // Guard de concorrência: wake events (focus + visibilitychange + pageshow
    // chegam juntos) e o backoff podem disparar start() em paralelo.
    if (this.isStarting) return;
    this.isStarting = true;
    this.intentionallyStopped = false;

    try {
      // UserAgent.start() é no-op quando o UA já foi iniciado ("already
      // started") — NÃO reabre o WebSocket. Toda reconexão passava por aqui,
      // mandava REGISTER num socket morto e a cadeia de backoff morria: ramal
      // desregistrado até o F5. Já iniciado → reconnect() reabre o transport.
      if (this.sipUserAgent.state === UserAgentState.Started) {
        if (!this.sipUserAgent.isConnected()) {
          await this.sipUserAgent.reconnect();
        }
      } else {
        await this.sipUserAgent.start();
      }

      // Re-REGISTER pós-queda vai de Registered → Registered: o SIP.js não
      // muda o estado do Registerer quando o transport cai, então o
      // stateChange não dispara e o client ficava "desregistrado" com o
      // contato aceito no Asterisk (call() recusava com "SIP nao registrado").
      // O 200 OK do REGISTER é a confirmação que vale.
      await this.registerer.register({
        requestDelegate: {
          onAccept: () => {
            if (!this.isRegistered) this.markRegistered();
          },
        },
      });
    } catch (error) {
      console.error('[AciSipClient] Erro ao iniciar:', error);
      this.onUserAgentAction({ type: 'registrationFailed', payload: { error } });
      this.scheduleReconnectIfNeeded();
    } finally {
      this.isStarting = false;
    }
  }

  async stop() {
    this.intentionallyStopped = true;
    this.clearReconnectTimer();
    this.stopKeepAlive();
    if (this.unbindWakeEvents) {
      this.unbindWakeEvents();
      this.unbindWakeEvents = null;
    }

    try {
      if (this.isRegistered) {
        await this.registerer.unregister();
      }
      await this.sipUserAgent.stop();
    } catch (error) {
      console.error('[AciSipClient] Erro ao parar:', error);
    }
  }

  // ==================== RECONNECT ====================

  scheduleReconnectIfNeeded() {
    if (this.intentionallyStopped) return;

    this.clearReconnectTimer();

    // Sem teto de tentativas: desistir deixava o telefone morto até F5
    // (laptop que dormiu >10min). Backoff exponencial capado em 60s; o
    // expoente é clampado pra não estourar pra Infinity.
    this.reconnectAttempts++;
    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, Math.min(this.reconnectAttempts - 1, 10)),
      60000
    );

    this.reconnectTimer = setTimeout(() => this.attemptReconnect(), delay);
  }

  // Aba/rede/máquina voltou: reconecta na hora (sem esperar o backoff, cujo
  // timer pode ter sido congelado durante o sleep). Se ainda achamos que
  // estamos registrados, o socket pode ser um zumbi pós-sleep — um REGISTER
  // imediato ou confirma o contato ou derruba o transport (e o
  // handleDisconnect agenda a reconexão).
  handleWake(reason) {
    if (this.intentionallyStopped) return;

    if (this.isRegistered) {
      this.refreshRegistration();
      return;
    }

    console.log('[AciSipClient] Wake event:', reason, '— reconectando agora');
    this.clearReconnectTimer();
    this.reconnectAttempts = 0;
    this.attemptReconnect();
  }

  async attemptReconnect() {
    if (this.isRegistered) {
      this.reconnectAttempts = 0;
      return;
    }

    try {
      await this.start();
    } catch (error) {
      console.error('[AciSipClient] Erro ao reconectar:', error.message);
      this.scheduleReconnectIfNeeded();
    }
  }

  clearReconnectTimer() {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  // ==================== INCOMING CALL ====================

  handleIncomingCall(invitation) {
    // Auto-reject duplicate INVITEs (Asterisk fork after re-registration sends multiple)
    const INCOMING_COOLDOWN_MS = 2000;
    if (this.activeCall || (Date.now() - this.lastCallEndedAt < INCOMING_COOLDOWN_MS)) {
      invitation.reject({ statusCode: 486, reasonPhrase: 'Busy Here' });
      return;
    }

    const callerNumber = invitation.remoteIdentity?.uri?.user || 'unknown';
    const callerDisplayName = invitation.remoteIdentity?.displayName || null;

    // A pausa no Asterisk só tira o ramal das FILAS: ligação direta de outro
    // ramal, transferência e URA que disca o ramal tocavam com o agente em
    // pausa (chamado Aliança/conta 161, ramal 9022 → agente em "Banheiro").
    // Em pausa, toda chamada recebida é recusada com 486 (quem liga ouve
    // ocupado) e o atendente fica sabendo quem tentou. Vale só pro discador
    // web — aparelho físico no mesmo ramal continua tocando.
    if (this.params.isPaused()) {
      invitation.reject({ statusCode: 486, reasonPhrase: 'Busy Here' }).catch(error => {
        console.warn('[AciSipClient] Erro ao recusar chamada em pausa:', error.message);
      });
      this.onUserAgentAction({
        type: 'incomingRejectedWhilePaused',
        payload: { number: callerNumber, displayName: callerDisplayName },
      });
      return;
    }

    this.activeCall = invitation;
    this.direction = 'incoming';
    this.startTime = null;

    // Setup session events
    this.setupSessionEvents(invitation);

    // Play ringtone
    this.playRingtone();

    // Emit to useAciSipConnection
    this.onUserAgentAction({
      type: 'newRTCSession',
      payload: {
        session: {
          direction: 'incoming',
          remote_identity: {
            uri: { user: callerNumber },
            display_name: callerDisplayName,
          },
        },
      },
    });

    // Emit bus event directly (bypasses composable ref scoping issues during HMR)
    emitter.emit(BUS_EVENTS.TELEPHONY_INCOMING_CALL, {
      number: callerNumber,
      displayName: callerDisplayName,
    });

    this.onCallAction({
      type: 'incoming',
      from: callerNumber,
      displayName: callerDisplayName,
    });
  }

  // ==================== CALL ====================

  call(options = {}) {
    if (!this.isRegistered) {
      throw new Error('SIP nao registrado');
    }

    if (this.activeCall) {
      throw new Error('Ja existe uma chamada ativa');
    }

    const { to, onCallAction = () => {}, extraHeaders = {} } = options;

    if (!to) {
      throw new Error('Numero de destino nao fornecido');
    }

    this.onCallAction = onCallAction;
    this.currentDestination = to;

    if (!this.audioPlayerElement) {
      this.setAudioElement();
    }

    const cleanNumber = AciSipClient.cleanDialNumber(to);
    const targetUri = this.buildDialUri(to);

    // Build extra headers array for SIP INVITE
    const sipExtraHeaders = [];
    Object.entries(extraHeaders).forEach(([key, value]) => {
      // Add X- prefix if not present
      const headerName = key.startsWith('X-') ? key : `X-${key}`;
      sipExtraHeaders.push(`${headerName}: ${value}`);
    });

    const inviter = new Inviter(this.sipUserAgent, targetUri, {
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
      extraHeaders: sipExtraHeaders,
    });

    this.activeCall = inviter;
    this.direction = 'outgoing';
    this.startTime = null;

    // Setup session events
    this.setupSessionEvents(inviter);

    // requestDelegate.onReject inspeciona a resposta SIP crua: a Meta recusa
    // chamadas WhatsApp sem permissão aprovada com 403 + reason "No Approved
    // Call Permission Found". Nesse caso emite 'permission_required' pra que o
    // front dispare o pedido de permissão automaticamente (rede de segurança).
    const inviteOptions = {
      requestDelegate: {
        onReject: response => {
          const statusCode = response?.message?.statusCode;
          const reason = response?.message?.reasonPhrase || '';
          if (statusCode === 403 && /call permission/i.test(reason)) {
            this.emitCallEvent({
              type: 'permission_required',
              payload: { number: cleanNumber, reason },
            });
            return;
          }
          // Código da recusa no console e no toast: é a evidência que o
          // suporte VoIP precisa pra rastrear falha intermitente de rota.
          console.warn(`[AciSipClient] INVITE recusado para ${cleanNumber}: ${statusCode} ${reason}`);
          this.emitCallEvent({
            type: 'rejected',
            payload: { statusCode, reason, number: cleanNumber },
          });
        },
      },
    };

    // Send INVITE
    inviter.invite(inviteOptions).catch(error => {
      console.error('[AciSipClient] Erro ao discar:', error);
      this.callDisposition = 'failed';
      this.emitCallEvent({ type: 'failed', payload: { cause: error.message } });
      this.cleanupSession();
    });

    // Emit to useAciSipConnection
    this.onUserAgentAction({
      type: 'newRTCSession',
      payload: {
        session: {
          direction: 'outgoing',
          remote_identity: {
            uri: { user: cleanNumber },
          },
        },
      },
    });
  }

  answer() {
    if (!this.activeCall) {
      console.error('[AciSipClient] Nao ha chamada para atender');
      throw new Error('Nao ha chamada para atender');
    }

    this.stopRingtone();

    if (!this.audioPlayerElement) {
      this.setAudioElement();
    }

    this.activeCall.accept({
      sessionDescriptionHandlerOptions: {
        constraints: { audio: true, video: false },
      },
    }).catch(err => {
      console.error('[AciSipClient] Erro ao atender:', err);
      throw err;
    });
  }

  hangup() {

    this.stopRingtone();

    if (!this.activeCall) {
      console.warn('[AciSipClient] Nenhuma chamada ativa para encerrar');
      return;
    }

    const session = this.activeCall;
    const state = session.state;
    let request;

    try {
      if (state === SessionState.Initial || state === SessionState.Establishing) {
        // Incoming call not answered yet - reject it
        if (this.direction === 'incoming') {
          this.callDisposition = 'busy';
          request = session.reject({
            statusCode: 486,
            reasonPhrase: 'Busy Here',
          });
        } else {
          // Outgoing call not connected yet - cancel it
          this.callDisposition = 'cancel';
          request = session.cancel();
        }
      } else if (state === SessionState.Established) {
        // Connected call - send BYE
        // disposition already set to 'answered' when established
        request = session.bye();
      } else {
        // Terminating: um CANCEL/BYE anterior nunca foi confirmado. O SIP.js
        // não tem timer depois do 1º 18x (transação em Proceeding), então
        // sem isto o botão de desligar não fazia nada até o F5.
        this.forceEndCall('o servidor não confirmou o encerramento da chamada');
        return;
      }
    } catch (error) {
      console.error('[AciSipClient] Erro ao encerrar chamada:', error);
      this.forceEndCall(`erro ao encerrar a chamada: ${error.message}`);
      return;
    }

    // cancel/bye/reject devolvem Promise — a rejeição escapava do try/catch.
    Promise.resolve(request).catch(error => {
      console.error('[AciSipClient] Erro ao encerrar chamada:', error);
      if (this.activeCall === session) {
        this.forceEndCall(`erro ao encerrar a chamada: ${error.message}`);
      }
    });

    this.armHangupWatchdog(session);
  }

  // Rede de segurança do hangup: o pedido saiu mas o Asterisk nunca respondeu
  // (CANCEL ignorado depois do 18x, BYE perdido). Passado o prazo, a sessão é
  // descartada localmente e o atendente é avisado — nunca fica preso.
  armHangupWatchdog(session) {
    this.clearHangupWatchdog();
    this.hangupWatchdog = setTimeout(() => {
      this.hangupWatchdog = null;
      if (this.activeCall === session) {
        console.warn(`[AciSipClient] Encerramento sem confirmação em ${HANGUP_CONFIRM_TIMEOUT_MS}ms (estado ${session.state}) — descartando localmente`);
        this.forceEndCall('o servidor não confirmou o encerramento da chamada');
      }
    }, HANGUP_CONFIRM_TIMEOUT_MS);
  }

  clearHangupWatchdog() {
    if (this.hangupWatchdog) {
      clearTimeout(this.hangupWatchdog);
      this.hangupWatchdog = null;
    }
  }

  /**
   * Encerra a chamada do lado do client quando o servidor não vai (ou não
   * pode) confirmar: emite 'failed' com o motivo (toast pro atendente),
   * libera o estado e só então descarta a sessão no SIP.js. O dispose roda
   * depois do cleanup, então o Terminated que ele gerar não emite 'ended'
   * de novo (o listener checa activeCall === session).
   */
  forceEndCall(cause) {
    const session = this.activeCall;
    const consultation = this.consultationCall;
    if (!session) return;

    this.disconnectAudio();
    this.callDisposition = 'failed';
    this.emitCallEvent({ type: 'failed', payload: { cause } });
    this.cleanupSession();

    [session, consultation].forEach(s => {
      if (!s) return;
      Promise.resolve()
        .then(() => s.dispose())
        .catch(error => {
          console.warn('[AciSipClient] Erro ao descartar sessão:', error.message);
        });
    });
  }

  // ==================== HOLD ====================

  async hold() {
    if (!this.activeCall) {
      throw new Error('Nao ha chamada ativa para colocar em espera');
    }

    if (this.localHold) {
      return false;
    }

    try {
      const sessionDescriptionHandler = this.activeCall.sessionDescriptionHandler;
      if (!sessionDescriptionHandler) {
        throw new Error('Session description handler nao disponivel');
      }

      // Send re-INVITE with hold
      await this.activeCall.invite({
        sessionDescriptionHandlerModifiers: [
          description => {
            // Modify SDP to sendonly for hold
            description.sdp = description.sdp.replace(/a=sendrecv/g, 'a=sendonly');
            return description;
          },
        ],
      });

      this.localHold = true;
      this.emitCallEvent({ type: 'hold' });
      return true;
    } catch (error) {
      console.error('[AciSipClient] Hold falhou:', error);
      this.emitCallEvent({ type: 'hold_failed' });
      return false;
    }
  }

  async unhold() {
    if (!this.activeCall) {
      throw new Error('Nao ha chamada ativa para retomar');
    }

    if (!this.localHold) {
      return false;
    }

    try {
      // Send re-INVITE without hold modifiers
      await this.activeCall.invite({
        sessionDescriptionHandlerModifiers: [
          description => {
            // Modify SDP to sendrecv for unhold
            description.sdp = description.sdp.replace(/a=sendonly/g, 'a=sendrecv');
            description.sdp = description.sdp.replace(/a=inactive/g, 'a=sendrecv');
            return description;
          },
        ],
      });

      this.localHold = false;
      this.emitCallEvent({ type: 'unhold' });
      return true;
    } catch (error) {
      console.error('[AciSipClient] Unhold falhou:', error);
      this.emitCallEvent({ type: 'unhold_failed' });
      return false;
    }
  }

  isOnHold() {
    return this.localHold;
  }

  toggleHold() {
    if (!this.activeCall) return false;

    if (this.localHold) {
      this.unhold();
      return false;
    }
    this.hold();
    return true;
  }

  // ==================== DIAL TARGET ====================

  // Preserva * e # — feature codes do Asterisk (*8000) e atalhos de fila
  // (#600, *600) precisam chegar inteiros, tanto na ligação quanto na
  // transferência (REFER), senão "#9029" vira o ramal "9029".
  static cleanDialNumber(number) {
    return String(number).replace(/[^\d+*#]/g, '');
  }

  buildDialUri(number) {
    const cleanNumber = AciSipClient.cleanDialNumber(number);
    // # é reservado em URIs (fragment delimiter) — precisa virar %23. * já é
    // permitido no userinfo da SIP URI (RFC 3261), mas encodeURIComponent
    // tambem o preserva, entao usar nele e seguro.
    const userPart = encodeURIComponent(cleanNumber);
    const targetUri = UserAgent.makeURI(`sip:${userPart}@${this.params.sipServer}`);
    if (!targetUri) {
      throw new Error(`URI SIP invalida: sip:${cleanNumber}@${this.params.sipServer}`);
    }
    return targetUri;
  }

  // ==================== TRANSFER ====================

  async transfer(target) {
    if (!this.activeCall) {
      throw new Error('Nao ha chamada ativa para transferir');
    }

    if (!target) {
      throw new Error('Destino da transferencia nao fornecido');
    }

    const cleanTarget = AciSipClient.cleanDialNumber(target);
    const targetUri = this.buildDialUri(target);

    try {
      const referOptions = {
        requestDelegate: {
          onAccept: () => {
            if (this.transferCleanupTimeout) {
              clearTimeout(this.transferCleanupTimeout);
              this.transferCleanupTimeout = null;
            }
            try {
              if (this.activeCall && this.activeCall.state === SessionState.Established) {
                this.activeCall.bye();
              }
            } catch (byeError) {
              console.warn('[AciSipClient] Erro ao enviar BYE pos-blind-transfer:', byeError);
              this.emitCallEvent({ type: 'ended' });
              this.cleanupSession();
            }
          },
          onReject: (response) => {
            console.error('[AciSipClient] Blind REFER rejected:', response.message.statusCode);
            this.emitCallEvent({ type: 'transfer_failed', target: cleanTarget });
          },
        },
        delegate: {
          onNotify: (notification) => {
            const body = notification.request.body || '';
            notification.accept();

            // Wait for 2xx in sipfrag before sending BYE
            if (body.match(/SIP\/2\.0\s+2\d\d/)) {
              console.log('[AciSipClient] Blind transfer confirmed via NOTIFY');
              if (this.transferCleanupTimeout) {
                clearTimeout(this.transferCleanupTimeout);
                this.transferCleanupTimeout = null;
              }
              try {
                if (this.activeCall && this.activeCall.state === SessionState.Established) {
                  this.activeCall.bye();
                }
              } catch (byeError) {
                console.warn('[AciSipClient] Erro ao enviar BYE pos-transfer:', byeError);
                this.emitCallEvent({ type: 'ended' });
                this.cleanupSession();
              }
            }
          },
        },
      };

      await this.activeCall.refer(targetUri, referOptions);
      this.emitCallEvent({ type: 'transfer_accepted', target: cleanTarget });

      // Fallback: if NOTIFY never arrives, send BYE after 10s
      this.transferCleanupTimeout = setTimeout(() => {
        console.warn('[AciSipClient] Blind transfer NOTIFY timeout, forcing BYE');
        try {
          if (this.activeCall && this.activeCall.state === SessionState.Established) {
            this.activeCall.bye();
          }
        } catch (e) {
          this.emitCallEvent({ type: 'ended' });
          this.cleanupSession();
        }
      }, 10000);
    } catch (error) {
      console.error('[AciSipClient] Transferencia falhou:', error);
      this.emitCallEvent({ type: 'transfer_failed', target: cleanTarget, error });
    }
  }

  // ==================== ATTENDED TRANSFER ====================

  /**
   * Start an attended (consultative) transfer.
   * Places the original call on hold and initiates a consultation call to the target.
   */
  async attendedTransfer(target) {
    if (!this.activeCall) {
      throw new Error('Nao ha chamada ativa para transferir');
    }

    if (!target) {
      throw new Error('Destino da transferencia nao fornecido');
    }

    if (this.consultationCall) {
      throw new Error('Ja existe uma consulta em andamento');
    }

    const cleanTarget = AciSipClient.cleanDialNumber(target);
    const targetUri = this.buildDialUri(target);

    try {
      // Step 1: Hold the original call
      if (!this.localHold) {
        const holdSuccess = await this.hold();
        if (!holdSuccess) {
          throw new Error('Falha ao colocar chamada original em espera');
        }
      }

      this.isAttendedTransferInProgress = true;

      // Step 2: Create consultation audio element
      if (!this.consultationAudioElement) {
        this.consultationAudioElement = document.createElement('audio');
        this.consultationAudioElement.autoplay = true;
        this.consultationAudioElement.volume = this.outputVolume;
        document.body.appendChild(this.consultationAudioElement);
      }

      // Step 3: Create and send consultation INVITE
      const inviter = new Inviter(this.sipUserAgent, targetUri, {
        sessionDescriptionHandlerOptions: {
          constraints: { audio: true, video: false },
        },
      });

      this.consultationCall = inviter;

      // Setup consultation session events
      this.setupConsultationSessionEvents(inviter);

      this.emitCallEvent({ type: 'consultation_calling', target: cleanTarget });
      await inviter.invite();
    } catch (error) {
      console.error('[AciSipClient] Erro ao iniciar transferencia assistida:', error);
      this.emitCallEvent({ type: 'consultation_failed', error: error.message });
      this.cleanupConsultation();

      // Try to unhold original call
      if (this.activeCall && this.localHold) {
        try {
          await this.unhold();
        } catch (unholdErr) {
          console.error('[AciSipClient] Erro ao retomar chamada apos falha de consulta:', unholdErr);
        }
      }
    }
  }

  /**
   * Setup events for the consultation session.
   */
  setupConsultationSessionEvents(session) {

    session.stateChange.addListener(state => {

      switch (state) {
        case SessionState.Establishing:
          this.emitCallEvent({ type: 'consultation_progress' });
          break;

        case SessionState.Established:
          this.connectConsultationAudio();
          this.emitCallEvent({ type: 'consultation_established' });
          break;

        case SessionState.Terminated:
          this.disconnectConsultationAudio();

          if (this.consultationCall === session) {
            // If transfer was not completed, auto-unhold original
            if (this.isAttendedTransferInProgress && this.activeCall) {
              this.isAttendedTransferInProgress = false;
              this.cleanupConsultation();
              this.emitCallEvent({ type: 'consultation_ended' });

              if (this.localHold) {
                this.unhold().catch(err => {
                  console.error('[AciSipClient] Erro ao retomar chamada apos consulta:', err);
                });
              }
            } else {
              this.cleanupConsultation();
            }
          }
          break;
      }
    });
  }

  /**
   * Complete the attended transfer using REFER with Replaces.
   * The original session refers to the consultation session, bridging the two remote parties.
   */
  async completeAttendedTransfer() {
    if (!this.activeCall) {
      throw new Error('Nao ha chamada original ativa');
    }

    if (!this.consultationCall) {
      throw new Error('Nao ha consulta ativa para completar');
    }

    if (this.consultationCall.state !== SessionState.Established) {
      throw new Error('Consulta nao esta conectada');
    }

    try {
      // REFER the original call to the consultation session.
      // SIP.js automatically adds the Replaces header when the target is a Session.

      // Use delegate to track REFER progress via NOTIFY messages
      const referOptions = {
        requestDelegate: {
          onAccept: () => {
            this.handleTransferSuccess();
          },
          onReject: (response) => {
            console.error('[AciSipClient] Attended REFER rejected:', response.message.statusCode);
            this.emitCallEvent({ type: 'attended_transfer_failed', error: 'REFER rejected' });
          },
        },
        delegate: {
          onNotify: (notification) => {
            // NOTIFY contains sipfrag with the transfer result
            const body = notification.request.body || '';
            notification.accept();

            console.log('[AciSipClient] Attended transfer NOTIFY sipfrag:', body);

            // Check for 2xx success (e.g., "SIP/2.0 200 OK")
            if (body.match(/SIP\/2\.0\s+2\d\d/)) {
              console.log('[AciSipClient] Attended transfer confirmed via NOTIFY');
              this.handleTransferSuccess();
            } else if (body.match(/SIP\/2\.0\s+[4-6]\d\d/)) {
              // 4xx, 5xx, 6xx are error responses
              console.warn('[AciSipClient] Attended transfer failed via NOTIFY:', body);
              this.handleTransferSuccess();
            }
            // 1xx (100 Trying, 180 Ringing) are progress - ignore
          },
        },
      };

      await this.activeCall.refer(this.consultationCall, referOptions);
      this.isAttendedTransferInProgress = false;

      // Fallback timeout: 15s to allow ICE/DTLS renegotiation on the Asterisk side
      this.transferCleanupTimeout = setTimeout(() => {
        console.warn('[AciSipClient] Attended transfer NOTIFY timeout, forcing cleanup');
        this.handleTransferSuccess();
      }, 15000);

      this.emitCallEvent({ type: 'attended_transfer_completed' });
    } catch (error) {
      console.error('[AciSipClient] Erro ao completar transferencia assistida:', error);
      this.emitCallEvent({ type: 'attended_transfer_failed', error: error.message });
    }
  }

  /**
   * Handle successful transfer - cleanup local sessions
   * Called after receiving NOTIFY with success or after timeout
   */
  handleTransferSuccess() {
    // Clear the timeout if it exists
    if (this.transferCleanupTimeout) {
      clearTimeout(this.transferCleanupTimeout);
      this.transferCleanupTimeout = null;
    }

    // Disconnect audio from both calls
    this.disconnectAudio();
    this.disconnectConsultationAudio();


    // Send BYE to consultation call (AgentA <-> AgentB leg)
    try {
      if (this.consultationCall && this.consultationCall.state === SessionState.Established) {
        this.consultationCall.bye();
      }
    } catch (consultByeError) {
      console.warn('[AciSipClient] Erro ao enviar BYE para consulta:', consultByeError);
    }

    // Send BYE to active call (Customer <-> AgentA leg)
    // The server should have already bridged Customer to AgentB at this point
    try {
      if (this.activeCall && this.activeCall.state === SessionState.Established) {
        this.activeCall.bye();
      }
    } catch (activeByeError) {
      console.warn('[AciSipClient] Erro ao enviar BYE para chamada original:', activeByeError);
    }

    // Force cleanup after a short delay
    setTimeout(() => {
      this.cleanupConsultation();
      this.emitCallEvent({ type: 'ended' });
      this.cleanupSession();
    }, 500);
  }

  /**
   * Cancel the attended transfer.
   * Terminates the consultation call and unholds the original.
   */
  async cancelAttendedTransfer() {

    this.isAttendedTransferInProgress = false;

    // Terminate consultation call
    if (this.consultationCall) {
      try {
        const consultState = this.consultationCall.state;
        if (consultState === SessionState.Initial || consultState === SessionState.Establishing) {
          this.consultationCall.cancel();
        } else if (consultState === SessionState.Established) {
          this.consultationCall.bye();
        }
      } catch (err) {
        console.warn('[AciSipClient] Erro ao encerrar consulta:', err);
      }
    }

    this.cleanupConsultation();
    this.emitCallEvent({ type: 'consultation_cancelled' });

    // Unhold original call
    if (this.activeCall && this.localHold) {
      try {
        await this.unhold();
      } catch (err) {
        console.error('[AciSipClient] Erro ao retomar chamada original:', err);
      }
    }
  }

  // ==================== CONSULTATION AUDIO ====================

  connectConsultationAudio() {
    if (!this.consultationAudioElement) {
      this.consultationAudioElement = document.createElement('audio');
      this.consultationAudioElement.autoplay = true;
      this.consultationAudioElement.volume = this.outputVolume;
      document.body.appendChild(this.consultationAudioElement);
    }

    this.disconnectConsultationAudio();

    const sdh = this.consultationCall?.sessionDescriptionHandler;
    if (!sdh) {
      console.error('[AciSipClient] Consultation session description handler nao disponivel');
      return;
    }

    const pc = sdh.peerConnection;
    if (!pc) {
      console.error('[AciSipClient] Consultation PeerConnection nao disponivel');
      return;
    }

    const remoteStream = new MediaStream();
    pc.getReceivers().forEach(receiver => {
      if (receiver.track) {
        remoteStream.addTrack(receiver.track);
      }
    });

    if (remoteStream.getTracks().length > 0) {
      this.consultationAudioElement.srcObject = remoteStream;
    }
  }

  disconnectConsultationAudio() {
    if (!this.consultationAudioElement?.srcObject) return;

    this.consultationAudioElement.srcObject.getTracks().forEach(track => track.stop());
    this.consultationAudioElement.srcObject = null;
  }

  cleanupConsultation() {
    this.disconnectConsultationAudio();

    if (this.consultationAudioElement) {
      this.consultationAudioElement.remove();
      this.consultationAudioElement = null;
    }

    this.consultationCall = null;
  }

  // ==================== SESSION EVENTS ====================

  setupSessionEvents(session) {

    session.stateChange.addListener(state => {

      switch (state) {
        case SessionState.Establishing:
          this.emitCallEvent({ type: 'progress' });
          break;

        case SessionState.Established:
          this.stopRingtone();
          this.startTime = new Date();
          this.callDisposition = 'answered';
          this.connectAudio();
          this.emitCallEvent({ type: 'accepted' });
          this.emitCallEvent({ type: 'confirmed' });
          break;

        case SessionState.Terminating:
          break;

        case SessionState.Terminated:
          this.stopRingtone();
          this.disconnectAudio();

          if (this.activeCall === session) {
            // If original call ends during consultation, cancel the consultation too
            if (this.consultationCall && this.isAttendedTransferInProgress) {
              try {
                const consultState = this.consultationCall.state;
                if (consultState === SessionState.Initial || consultState === SessionState.Establishing) {
                  this.consultationCall.cancel();
                } else if (consultState === SessionState.Established) {
                  this.consultationCall.bye();
                }
              } catch (err) {
                console.warn('[AciSipClient] Erro ao cancelar consulta apos fim da chamada original:', err);
              }
            }

            // ✅ Emitir evento 'ended' ANTES de limpar a sessão
            this.emitCallEvent({ type: 'ended' });
            this.cleanupSession();
          }
          break;
      }
    });

    // Monitor PeerConnection for new tracks (re-INVITE after transfer)
    const monitorPeerConnection = () => {
      const sdh = session.sessionDescriptionHandler;
      if (!sdh) return;

      const pc = sdh.peerConnection;
      if (!pc) return;

      pc.addEventListener('track', (event) => {
        console.log('[AciSipClient] New track received (re-INVITE):', event.track.kind);
        if (this.activeCall === session && event.track.kind === 'audio') {
          this.connectAudio();
        }
      });

      pc.addEventListener('iceconnectionstatechange', () => {
        const iceState = pc.iceConnectionState;
        this.iceConnectionState = iceState;
        console.log('[AciSipClient] ICE connection state:', iceState);

        if ((iceState === 'connected' || iceState === 'completed') && this.activeCall === session) {
          this.connectAudio();
        }

        if (iceState === 'failed') {
          console.error('[AciSipClient] ICE connection failed after re-INVITE');
        }
      });
    };

    if (session.sessionDescriptionHandler) {
      monitorPeerConnection();
    } else {
      // Wait for session to be established, then monitor
      session.stateChange.addListener((state) => {
        if (state === SessionState.Established) {
          setTimeout(() => monitorPeerConnection(), 100);
        }
      });
    }

    session.sessionDescriptionHandlerModifiersReInvite = [];
  }

  /**
   * Emit call event to both specific and global callbacks
   */
  emitCallEvent(event) {
    // Call specific callback (set by call())
    this.onCallAction(event);
    // Call global callback (set in constructor)
    this.params.onCallEvent(event);
  }

  getActiveCall() {
    return this.activeCall;
  }

  cleanupSession() {
    this.stopRingtone();
    this.clearHangupWatchdog();
    this.lastCallEndedAt = Date.now();

    // Clear transfer cleanup timeout if exists
    if (this.transferCleanupTimeout) {
      clearTimeout(this.transferCleanupTimeout);
      this.transferCleanupTimeout = null;
    }

    // Cleanup any active consultation
    this.cleanupConsultation();
    this.isAttendedTransferInProgress = false;

    this.activeCall = null;
    this.direction = null;
    this.startTime = null;
    this.endTime = null;
    this.currentDestination = null;
    this.iceConnectionState = 'new';
    this.failed = false;
    this.localHold = false;
    this.callDisposition = null;
    this.onCallAction = () => {};
  }

  // ==================== AUDIO ====================

  setAudioElement(domNode) {
    if (domNode === undefined) {
      this.audioPlayerElement = document.createElement('audio');
      document.body.appendChild(this.audioPlayerElement);
    } else if (typeof domNode === 'string') {
      const found = document.querySelector(domNode);
      if (!found || found.tagName.toLowerCase() !== 'audio') {
        throw new Error('Seletor DOM invalido para elemento audio');
      }
      this.audioPlayerElement = found;
    } else {
      this.audioPlayerElement = domNode;
    }

    this.audioPlayerElement.defaultMuted = false;
    this.audioPlayerElement.autoplay = true;
    this.audioPlayerElement.volume = this.outputVolume;
  }

  connectAudio() {
    if (!this.audioPlayerElement) {
      this.setAudioElement();
    }

    this.disconnectAudio();

    const sessionDescriptionHandler = this.activeCall?.sessionDescriptionHandler;
    if (!sessionDescriptionHandler) {
      console.error('[AciSipClient] Session description handler nao disponivel');
      return;
    }

    const peerConnection = sessionDescriptionHandler.peerConnection;
    if (!peerConnection) {
      console.error('[AciSipClient] PeerConnection nao disponivel');
      return;
    }

    // Get remote stream
    const remoteStream = new MediaStream();
    peerConnection.getReceivers().forEach(receiver => {
      if (receiver.track) {
        remoteStream.addTrack(receiver.track);
      }
    });

    if (remoteStream.getTracks().length > 0) {
      this.audioPlayerElement.srcObject = remoteStream;
    }

    // Apply mic mute state
    this.setMicMuted(this.micMuted);
  }

  disconnectAudio() {
    if (!this.audioPlayerElement?.srcObject) return;

    // Only detach from audio element — do NOT stop remote tracks
    // Stopping tracks permanently destroys them, breaking audio after re-INVITE
    this.audioPlayerElement.srcObject = null;
  }

  setOutputVolume(value) {
    const volume = Math.max(0, Math.min(1, parseInt(value, 10) / 100));
    this.outputVolume = volume;

    if (this.audioPlayerElement) {
      this.audioPlayerElement.volume = volume;
    }
  }

  getOutputVolume() {
    return this.outputVolume * 100;
  }

  setMicMuted(muted) {
    this.micMuted = muted;

    const sessionDescriptionHandler = this.activeCall?.sessionDescriptionHandler;
    if (!sessionDescriptionHandler) return;

    const peerConnection = sessionDescriptionHandler.peerConnection;
    if (!peerConnection) return;

    peerConnection.getSenders().forEach(sender => {
      if (sender.track && sender.track.kind === 'audio') {
        sender.track.enabled = !muted;
      }
    });
  }

  getMicMuted() {
    return this.micMuted;
  }

  toggleMute() {
    this.setMicMuted(!this.micMuted);
    return this.micMuted;
  }

  // ==================== DTMF ====================

  sendDTMF(tone) {
    if (!this.activeCall) {
      throw new Error('Nao ha chamada ativa para enviar DTMF');
    }

    const sessionDescriptionHandler = this.activeCall.sessionDescriptionHandler;
    if (!sessionDescriptionHandler) {
      throw new Error('Session description handler nao disponivel');
    }

    // Send DTMF via RTP (in-band)
    sessionDescriptionHandler.sendDtmf(tone);
  }

  // ==================== UTILS ====================

  isIceReady() {
    return (
      this.iceConnectionState === 'connected' ||
      this.iceConnectionState === 'completed'
    );
  }

  getCallDuration() {
    if (!this.startTime) return 0;
    return Math.floor((new Date() - this.startTime) / 1000);
  }

  formatDuration(seconds) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
}
